<script setup lang="ts">
import { ref, computed, watch, nextTick } from 'vue';
import { CatalogueItemCategory, Currency } from '@/models/Catalogue';
import type {
  CatalogueItem,
  CatalogueItemGroup,
  CatalogueItemCustomField,
} from '@/models/Catalogue';
import gettext from '@/i18n/gettext';
import {
  requiredRule,
  floatRule,
  integerRule,
  positiveFloatRule,
  positiveIntegerRule,
  ipClassRule,
} from '@/helpers/FormRules';
import GroupAutocomplete from '@/components/catalogue/GroupAutocomplete.vue';
import { Permission } from '@/models/Roles';
import useAuthStore from '@/store/AuthStore';

const { $gettext } = gettext;
const authStore = useAuthStore();

const cleanItemData = (item?: CatalogueItem): Partial<CatalogueItem> => {
  if (!item) {
    // Return default values for new item creation - only required fields have defaults
    return {
      currency: Currency.EUR,
    };
  }

  return {
    uuid: item.uuid,
    name: item.name,
    description: item.description,
    category: item.category,
    groupUuid: item.groupUuid,
    cost: item.cost || 0,
    currency: item.currency || Currency.EUR,
    hyperlink: item.hyperlink,
    erpId: item.erpId,
    drawingNumber: item.drawingNumber,
    activationPrinciple: item.activationPrinciple,
    gripControlSensor: item.gripControlSensor,
    distanceSensor: item.distanceSensor,
    weightG: item.weightG,
    strokeMm: item.strokeMm,
    maxGripForceN: item.maxGripForceN,
    ipClass: item.ipClass,
    materialType: item.materialType,
    airSupplyNeeded: item.airSupplyNeeded,
    payloadKg: item.payloadKg,
    reachMm: item.reachMm,
    dof: item.dof,
    vacuumSupplyNeeded: item.vacuumSupplyNeeded,
    pneumaticTubeDiameterMm: item.pneumaticTubeDiameterMm,
    robotVersion: item.robotVersion,
    customFields: item.customFields,
  };
};

const props = defineProps<{
  modelValue: boolean;
  item?: CatalogueItem;
  groups: CatalogueItemGroup[];
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void;
  (e: 'save', item: CatalogueItem): void;
}>();

const form = ref<Partial<CatalogueItem>>({
  name: props.item?.name || '',
  description: props.item?.description,
  category: props.item?.category,
  groupUuid: props.item?.groupUuid,
  cost: props.item?.cost || 0,
  currency: props.item?.currency || Currency.EUR,
  hyperlink: props.item?.hyperlink,
  erpId: props.item?.erpId,
  drawingNumber: props.item?.drawingNumber,
  activationPrinciple: props.item?.activationPrinciple,
  gripControlSensor: props.item?.gripControlSensor,
  distanceSensor: props.item?.distanceSensor,
  weightG: props.item?.weightG,
  strokeMm: props.item?.strokeMm,
  maxGripForceN: props.item?.maxGripForceN,
  ipClass: props.item?.ipClass,
  materialType: props.item?.materialType,
  airSupplyNeeded: props.item?.airSupplyNeeded,
  payloadKg: props.item?.payloadKg,
  reachMm: props.item?.reachMm,
  dof: props.item?.dof,
  vacuumSupplyNeeded: props.item?.vacuumSupplyNeeded,
  pneumaticTubeDiameterMm: props.item?.pneumaticTubeDiameterMm,
  robotVersion: props.item?.robotVersion,
});

interface CustomFieldRow {
  key: string;
  type: CatalogueItemCustomField['type'];
  value: string | number;
}

const dictToRows = (
  dict?: Record<string, CatalogueItemCustomField> | null,
): CustomFieldRow[] => {
  if (!dict) {
    return [];
  }
  return Object.entries(dict).map(([key, field]) => ({
    key,
    type: field.type,
    value: field.value,
  }));
};

const rowsToDict = (
  rows: CustomFieldRow[],
): Record<string, CatalogueItemCustomField> | null => {
  const valid = rows.filter((r) => r.key.trim() !== '');
  if (valid.length === 0) {
    return null;
  }
  const dict: Record<string, CatalogueItemCustomField> = {};
  for (const row of valid) {
    let { value } = row;
    if (row.type === 'string') {
      value = String(value);
    } else {
      value = Number(value);
    }
    dict[row.key.trim()] = { type: row.type, value };
  }
  return dict;
};

const customFieldRows = ref<CustomFieldRow[]>(
  dictToRows(props.item?.customFields),
);

const addCustomField = () => {
  customFieldRows.value.push({ key: '', type: 'string', value: '' });
};

const removeCustomField = (index: number) => {
  customFieldRows.value.splice(index, 1);
};

const handleTypeChange = (index: number) => {
  const row = customFieldRows.value[index];
  if (row.type === 'string') {
    row.value = String(row.value);
  } else {
    const num = Number(row.value);
    if (!isNaN(num) && row.value !== '') {
      row.value = num;
    }
  }
};

const uniqueKeyRule = (index: number) => (v: string) => {
  if (!v) {
    return true;
  }
  const trimmed = v.trim();
  const duplicate = customFieldRows.value.some(
    (row, i) => i !== index && row.key.trim() === trimmed,
  );
  return !duplicate || $gettext('Field name must be unique');
};

const formRef = ref();
const activeTab = ref('basic');

const dialogTitle = computed(() => {
  return props.item ? $gettext('Edit Item') : $gettext('Create Item');
});

const categoryOptions = computed(() => {
  const allCategories = Object.values(CatalogueItemCategory);
  const isPowerUser = authStore.hasPermission(Permission.UPDATE_CATALOGUE_ALL);

  if (isPowerUser) {
    return allCategories;
  }

  // Non-power users cannot set category to 'standard'
  return allCategories.filter((cat) => cat !== CatalogueItemCategory.STANDARD);
});

const currencySymbol = computed(() => {
  const symbols: Record<string, string> = {
    EUR: '€',
    USD: '$',
    GBP: '£',
    CHF: 'CHF',
    JPY: '¥',
    CNY: '¥',
  };
  return symbols[form.value.currency || Currency.EUR] || form.value.currency;
});

const handleSave = async () => {
  const { valid } = await formRef.value.validate();

  if (!valid) {
    // Find the first field with an error
    const firstErrorField = formRef.value.$el.querySelector(
      '.v-field--error input, .v-field--error textarea, .v-field--error .v-select',
    );
    if (firstErrorField) {
      // Find which tab contains this field by checking each tab's content
      let tabToSwitch = 'basic'; // default to basic tab

      // Get all v-window-item elements and check which one contains the error field
      const windowItems = formRef.value.$el.querySelectorAll('.v-window-item');

      for (let i = 0; i < windowItems.length; i++) {
        const item = windowItems[i];

        if (item.contains(firstErrorField)) {
          // Get the value from the data-value attribute or check the item's position
          const value =
            item.getAttribute('data-value') || item.getAttribute('value');

          // If we can't get the value from attributes, determine by position
          if (!value) {
            if (i === 0) {
              tabToSwitch = 'basic';
            } else if (i === 1) {
              tabToSwitch = 'technical';
            } else if (i === 2) {
              tabToSwitch = 'capabilities';
            }
          } else {
            tabToSwitch = value;
          }
          break;
        }
      }

      // Switch to the tab and wait for it to render
      activeTab.value = tabToSwitch;

      // Wait longer for the tab transition to complete
      await new Promise<void>((resolve) => {
        setTimeout(resolve, 300);
      });
      await nextTick();

      // Now focus on the field and scroll to it
      firstErrorField.focus();
      firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    return;
  }

  // Attach custom fields (convert rows to dict, filtering empty keys)
  form.value.customFields = rowsToDict(customFieldRows.value);

  // Clean up the form data before saving - remove empty strings and set them to null
  const cleanedForm = { ...form.value };

  // Convert empty strings to null for optional fields
  Object.keys(cleanedForm).forEach((key) => {
    const fieldKey = key as keyof typeof cleanedForm;
    if (cleanedForm[fieldKey] === '') {
      (cleanedForm as Record<string, unknown>)[fieldKey] = null;
    }
  });

  // When updating an existing item, merge with original data to ensure all fields are sent
  let finalItem: CatalogueItem;
  if (props.item?.uuid) {
    // For updates, merge the cleaned form data with the original item data
    // This ensures all fields are sent, even if they're not in the form
    finalItem = {
      ...props.item,
      ...cleanedForm,
    };
  } else {
    // For new items, just use the cleaned form data
    finalItem = cleanedForm as CatalogueItem;
  }

  emit('save', finalItem);
};

watch(
  () => props.modelValue,
  (isOpen) => {
    if (isOpen) {
      form.value = cleanItemData(props.item);
      customFieldRows.value = dictToRows(props.item?.customFields);
      activeTab.value = 'basic'; // Always start with basic tab
    }
  },
);

// Watch for changes to the item prop to update form when item data changes
watch(
  () => props.item,
  (newItem) => {
    if (props.modelValue) {
      form.value = cleanItemData(newItem);
      customFieldRows.value = dictToRows(newItem?.customFields);
      activeTab.value = 'basic'; // Always start with basic tab
    }
  },
);

const handleCancel = () => {
  emit('update:modelValue', false);
};
</script>

<template>
  <v-dialog
    :model-value="props.modelValue"
    max-width="800px"
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <v-card class="item-edit-card">
      <v-form ref="formRef" @submit.prevent="handleSave">
        <div class="pa-6">
          <h2 class="text-h5 mb-4">{{ dialogTitle }}</h2>

          <v-divider class="my-4" />

          <v-tabs v-model="activeTab">
            <v-tab value="basic">
              {{ $gettext('Basic Information') }}
            </v-tab>
            <v-tab value="technical">
              {{ $gettext('Technical Specification') }}
            </v-tab>
            <v-tab value="capabilities">
              {{ $gettext('Capabilities & Requirements') }}
            </v-tab>
          </v-tabs>

          <v-window v-model="activeTab" class="pt-4">
            <v-window-item value="basic">
              <v-row>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="form.name"
                    :label="$gettext('Name')"
                    :rules="[requiredRule]"
                    required
                  />
                </v-col>
                <v-col cols="12" md="3">
                  <v-text-field
                    v-model.number="form.cost"
                    :label="`${$gettext('Cost')} (${currencySymbol})`"
                    type="number"
                    step="0.01"
                    min="0"
                    :rules="[requiredRule, positiveFloatRule]"
                    required
                  />
                </v-col>
                <v-col cols="12" md="3">
                  <v-select
                    v-model="form.currency"
                    :items="Object.values(Currency)"
                    :label="$gettext('Currency')"
                    :rules="[requiredRule]"
                    required
                  />
                </v-col>
                <v-col cols="12">
                  <v-textarea
                    v-model="form.description"
                    :label="$gettext('Description')"
                    rows="3"
                    auto-grow
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <v-select
                    v-model="form.category"
                    :items="categoryOptions"
                    :label="$gettext('Category')"
                    :rules="[requiredRule]"
                    required
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <GroupAutocomplete
                    v-model="form.groupUuid"
                    :groups="props.groups"
                    :label="$gettext('Group')"
                    :rules="[requiredRule]"
                    required
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="form.erpId"
                    :label="$gettext('Xentral ID')"
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="form.drawingNumber"
                    :label="$gettext('Drawing Number')"
                  />
                </v-col>
                <v-col cols="12">
                  <v-text-field
                    v-model="form.hyperlink"
                    :label="$gettext('Hyperlink')"
                    type="url"
                  />
                </v-col>
              </v-row>
            </v-window-item>

            <v-window-item value="technical">
              <v-row>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="form.activationPrinciple"
                    :label="$gettext('Activation Principle')"
                    clearable
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="form.materialType"
                    :label="$gettext('Material Type')"
                    clearable
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model.number="form.weightG"
                    :label="$gettext('Weight (g)')"
                    type="number"
                    min="0"
                    :rules="[positiveIntegerRule]"
                    clearable
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model.number="form.strokeMm"
                    :label="$gettext('Stroke (mm)')"
                    type="number"
                    min="0"
                    :rules="[positiveIntegerRule]"
                    clearable
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model.number="form.maxGripForceN"
                    :label="$gettext('Max Grip Force (N)')"
                    type="number"
                    min="0"
                    :rules="[positiveIntegerRule]"
                    clearable
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="form.ipClass"
                    :label="$gettext('IP Class')"
                    :placeholder="'IP65, IP6X'"
                    :rules="[ipClassRule]"
                    clearable
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model.number="form.payloadKg"
                    :label="$gettext('Payload (kg)')"
                    type="number"
                    min="0"
                    :rules="[positiveFloatRule]"
                    clearable
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model.number="form.reachMm"
                    :label="$gettext('Reach (mm)')"
                    type="number"
                    min="0"
                    :rules="[positiveIntegerRule]"
                    clearable
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <v-select
                    v-model="form.dof"
                    :items="[2, 3, 4, 5, 6, 7, 8]"
                    :label="$gettext('Degrees of Freedom')"
                    clearable
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model.number="form.pneumaticTubeDiameterMm"
                    :label="$gettext('Pneumatic Tube Diameter (mm)')"
                    type="number"
                    min="0"
                    :rules="[positiveIntegerRule]"
                    clearable
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <v-select
                    v-model="form.robotVersion"
                    :items="['2', '3']"
                    :label="$gettext('Robot Version')"
                    clearable
                  />
                </v-col>
              </v-row>

              <!-- Custom Fields -->
              <v-divider class="my-4" />
              <h3 class="text-subtitle-1 font-weight-medium mb-2">
                {{ $gettext('Custom Fields') }}
              </h3>
              <v-row
                v-for="(row, index) in customFieldRows"
                :key="index"
                align="center"
                dense
              >
                <v-col cols="12" md="4">
                  <v-text-field
                    v-model="row.key"
                    :label="$gettext('Name')"
                    :rules="[requiredRule, uniqueKeyRule(index)]"
                    density="compact"
                  />
                </v-col>
                <v-col cols="12" md="3">
                  <v-select
                    v-model="row.type"
                    :items="[
                      { title: $gettext('Text'), value: 'string' },
                      { title: $gettext('Number (Integer)'), value: 'integer' },
                      { title: $gettext('Number (Float)'), value: 'float' },
                    ]"
                    item-title="title"
                    item-value="value"
                    :label="$gettext('Type')"
                    density="compact"
                    @update:model-value="handleTypeChange(index)"
                  />
                </v-col>
                <v-col cols="12" md="4">
                  <v-text-field
                    v-if="row.type === 'string'"
                    v-model="row.value"
                    :label="$gettext('Value')"
                    :rules="[requiredRule]"
                    density="compact"
                  />
                  <v-text-field
                    v-else-if="row.type === 'integer'"
                    v-model.number="row.value"
                    :label="$gettext('Value')"
                    type="number"
                    :rules="[requiredRule, integerRule]"
                    density="compact"
                  />
                  <v-text-field
                    v-else
                    v-model.number="row.value"
                    :label="$gettext('Value')"
                    type="number"
                    :rules="[requiredRule, floatRule]"
                    density="compact"
                  />
                </v-col>
                <v-col cols="12" md="1" class="d-flex justify-center">
                  <v-btn
                    icon="mdi-delete"
                    size="small"
                    variant="text"
                    color="error"
                    @click="removeCustomField(index)"
                  />
                </v-col>
              </v-row>
              <v-btn
                variant="outlined"
                color="primary"
                prepend-icon="mdi-plus"
                size="small"
                class="mt-2"
                @click="addCustomField"
              >
                {{ $gettext('Add Custom Field') }}
              </v-btn>
            </v-window-item>

            <v-window-item value="capabilities">
              <v-row>
                <v-col cols="12" md="6">
                  <v-select
                    v-model="form.gripControlSensor"
                    :items="[
                      { title: $gettext('Not specified'), value: null },
                      { title: $gettext('Yes'), value: true },
                      { title: $gettext('No'), value: false },
                    ]"
                    item-title="title"
                    item-value="value"
                    :label="$gettext('Grip Control Sensor')"
                    clearable
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <v-select
                    v-model="form.distanceSensor"
                    :items="[
                      { title: $gettext('Not specified'), value: null },
                      { title: $gettext('Yes'), value: true },
                      { title: $gettext('No'), value: false },
                    ]"
                    item-title="title"
                    item-value="value"
                    :label="$gettext('Distance Sensor')"
                    clearable
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <v-select
                    v-model="form.airSupplyNeeded"
                    :items="[
                      { title: $gettext('Not specified'), value: null },
                      { title: $gettext('Yes'), value: true },
                      { title: $gettext('No'), value: false },
                    ]"
                    item-title="title"
                    item-value="value"
                    :label="$gettext('Air Supply Needed')"
                    clearable
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <v-select
                    v-model="form.vacuumSupplyNeeded"
                    :items="[
                      { title: $gettext('Not specified'), value: null },
                      { title: $gettext('Yes'), value: true },
                      { title: $gettext('No'), value: false },
                    ]"
                    item-title="title"
                    item-value="value"
                    :label="$gettext('Vacuum Supply Needed')"
                    clearable
                  />
                </v-col>
              </v-row>
            </v-window-item>
          </v-window>
        </div>
        <v-card-actions>
          <v-spacer />
          <v-btn color="grey-darken-1" variant="text" @click="handleCancel">
            {{ $gettext('Cancel') }}
          </v-btn>
          <v-btn color="primary" variant="text" @click="handleSave">
            {{ $gettext('Save') }}
          </v-btn>
        </v-card-actions>
      </v-form>
    </v-card>
  </v-dialog>
</template>

<style scoped>
.item-edit-card {
  max-height: calc(100vh - 150px);
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.item-edit-card :deep(.v-form) {
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.item-edit-card :deep(.v-form > .pa-6) {
  overflow-y: auto;
}
</style>
